(() => {
  if (window.__brillareGiftSleeveCartSyncLoadedV16) return;
  window.__brillareGiftSleeveCartSyncLoadedV16 = true;
  console.info('[Gift Sleeve] Cart Sync V1.6 loaded');

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let timer = null;
  let running = false;

  async function getCart() {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  function isSleeve(item) {
    return Boolean(item?.properties?._gift_sleeve_for_variant_id);
  }

  function parentVariantIdOfSleeve(item) {
    return Number(item?.properties?._gift_sleeve_for_variant_id || 0);
  }

  async function changeLine(key, quantity) {
    console.info('[Gift Sleeve] Syncing sleeve quantity', { key, quantity });

    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Unable to update gift sleeve.');
    }
    return result;
  }

  function getParentQuantity(cart, boxVariantId) {
    return (cart.items || [])
      .filter((item) => !isSleeve(item) && Number(item.variant_id || item.id) === Number(boxVariantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  function sleeveItemsForParent(cart, boxVariantId) {
    return (cart.items || []).filter(
      (item) => isSleeve(item) && parentVariantIdOfSleeve(item) === Number(boxVariantId)
    );
  }

  async function reconcileParent(cart, boxVariantId) {
    const parentQty = getParentQuantity(cart, boxVariantId);
    const sleeves = sleeveItemsForParent(cart, boxVariantId);
    const sleeveQty = sleeves.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    // Critical V1.6 rule:
    // Never remove a valid sleeve merely because the Gift Box does not carry
    // our private _gift_sleeve_group property. The sleeve already tells us
    // which Gift Box variant it belongs to via _gift_sleeve_for_variant_id.
    if (parentQty === sleeveQty) return false;

    // Parent removed: remove all associated complimentary sleeves.
    if (parentQty === 0) {
      for (const sleeve of sleeves) {
        if (Number(sleeve.quantity || 0) > 0) {
          await changeLine(sleeve.key, 0);
        }
      }
      return sleeves.length > 0;
    }

    // Parent quantity increased. Increase the first already-selected sleeve.
    if (sleeveQty < parentQty && sleeves.length > 0) {
      const first = sleeves[0];
      const delta = parentQty - sleeveQty;
      await changeLine(first.key, Number(first.quantity || 0) + delta);
      return true;
    }

    // Parent quantity decreased. Reduce sleeves from the last line backwards
    // until total complimentary sleeve quantity matches Gift Box quantity.
    if (sleeveQty > parentQty) {
      let excess = sleeveQty - parentQty;

      for (let i = sleeves.length - 1; i >= 0 && excess > 0; i -= 1) {
        const sleeve = sleeves[i];
        const current = Number(sleeve.quantity || 0);
        const reduction = Math.min(current, excess);
        const nextQty = current - reduction;
        await changeLine(sleeve.key, nextQty);
        excess -= reduction;
      }
      return true;
    }

    return false;
  }

  async function reconcile() {
    if (running) return;
    running = true;

    try {
      let cart = await getCart();

      const parentVariantIds = [...new Set(
        (cart.items || [])
          .filter(isSleeve)
          .map(parentVariantIdOfSleeve)
          .filter(Boolean)
      )];

      let changed = false;
      for (const boxVariantId of parentVariantIds) {
        if (await reconcileParent(cart, boxVariantId)) {
          changed = true;
          // Refresh cart state after each mutation so subsequent calculations
          // use the current Shopify cart rather than stale quantities.
          cart = await getCart();
        }
      }

      if (!changed) {
        console.debug('[Gift Sleeve] Cart quantities already aligned');
      }
    } catch (error) {
      console.error('[Gift Sleeve] Cart sync failed:', error);
    } finally {
      running = false;
    }
  }

  function schedule(delay = 180) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  }

  const observer = new MutationObserver(() => schedule(250));

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    schedule(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('gift-sleeve:added', () => schedule(150));
  document.addEventListener('cart:updated', () => schedule(250));
  document.addEventListener('cartUpdate', () => schedule(250));
})();
